import React from "react";
import GlobalStyles from "../styles/GlobalStyles";
import tw, { styled } from "twin.macro";
import "styled-components/macro";
import _ from "lodash";
import { useStaticQuery, graphql } from "gatsby";

import diamondbacksLogo from "../images/diamondbacks.png";
import Seo from "./Seo";

import Nav from "@shared/components/Nav";
import SidePanel from "@shared/components/SidePanel";
import Footer from "@shared/components/Footer";
import SlideshowHero from "@shared/components/SlideshowHero";
import DynamicContainer from "@shared/components/DynamicContainer";
import Breadcrumbs from "@shared/components/Breadcrumbs";
import Sponsors from "@shared/components/Sponsors";
import { addNumberSuffix } from "@shared/utils";

import Scroll from "@shared/components/Scroll";
import {
  aboutLinks,
  BACKUP_LOGO_URL,
  facilityLinks,
  getTeamLinks,
  HOME_TEAMS,
  MAIN_COLOR,
  SITE,
  socialMediaData,
  timelineAccount,
} from "../config";

const Main = styled.main`
  ${tw`relative min-h-screen`}
`;
const WhiteBanner = styled.div`
  ${tw`bg-white w-full h-8`}
`;
const Section = styled.section`
  ${tw`relative mx-auto min-h-[60vh] bg-[#EFEFEF]`}
  ${({ isHomePage }) => isHomePage && tw`bg-[#FFFFFF]`}
`;
const Container = styled(DynamicContainer)`
  ${tw`
    transition-[padding,gap]
    grid grid-cols-1
    py-4 sm:py-8
  `}
  ${({ isHomePage }) =>
    isHomePage
      ? tw`lg:grid-cols-[minmax(0,2fr),minmax(300px, 1fr)] gap-8 lg:gap-16 xl:gap-24 2xl:gap-32`
      : tw`lg:grid-cols-[minmax(0,3fr),minmax(0, 1fr)] gap-8 pt-0 sm:pt-0`}
`;

const ContentMain = styled.div`
  ${tw`bg-white w-full overflow-hidden `}

  ${({ noBg }) => noBg && tw`bg-transparent`}
`;

const ContentSide = styled.div`
  ${tw`w-full`}
`;

const Layout = ({
  seoTitle,
  seoDescription,
  seoTitlePrefix,
  children,
  noBg,
  location,
  pageTitle,
  useSeoForTitle,
  isHomePage = false,
}) => {
  const data = useStaticQuery(graphql`
    query LayoutQuery {
      # only homepage
      markdownRemark(frontmatter: { templateKey: { eq: "index-page" } }) {
        frontmatter {
          seo {
            title
            description
          }
          hero {
            watchLiveLink
            teamSeasonLink
            slides {
              image {
                childImageSharp {
                  gatsbyImageData(layout: FULL_WIDTH)
                }
              }
              dots {
                enabled
                size
                color
              }
              overlay {
                enabled
                color
                opacity
              }
              video {
                publicURL
              }
              headers
              blurb
              buttons {
                icon
                label
                url
                color
                width
              }
              cutout {
                image {
                  childImageSharp {
                    gatsbyImageData(layout: FULL_WIDTH)
                  }
                }
                position
                offset
              }
            }
          }
          sponsorslides {
            slideshowHeader
            header
            subHeader
            description
            subDescription
            phone
            email
            list {
              image {
                childImageSharp {
                  gatsbyImageData
                }
              }
              title
            }
          }
        }
      }
      # only home page
      allHomeGames: allMarkdownRemark(
        filter: {
          frontmatter: {
            templateKey: { eq: "game-post" }
            homeTeam: { in: ["Diamondbacks", "diamondbacks"] }
          }
        }
        sort: { fields: frontmatter___date, order: ASC }
      ) {
        nodes {
          id
          frontmatter {
            homeTeam
            awayTeam
            date
            yr
          }
        }
      }

      latestRosterPage: allSitePage(
        filter: { path: { regex: "/^/roster/[0-9]{4}$/" } }
        sort: { fields: path, order: DESC }
        limit: 1
      ) {
        nodes {
          path
          id
        }
      }
      latestSchedulePage: allSitePage(
        filter: { path: { regex: "/^/schedule/[0-9]{4}$/" } }
        sort: { fields: path, order: DESC }
        limit: 1
      ) {
        nodes {
          path
          id
        }
      }
      fmbaLeague: markdownRemark(
        frontmatter: { mdType: { eq: "league" }, name: { eq: "FMBA" } }
      ) {
        frontmatter {
          color
        }
      }
      # only homepage
      allGames: allMarkdownRemark(
        filter: { frontmatter: { templateKey: { eq: "game-post" } } }
        sort: { fields: frontmatter___date, order: ASC }
      ) {
        nodes {
          fields {
            slug
          }
          id
          frontmatter {
            title
            homeTeam
            awayTeam
            yr
            date
            liveScoreLink
            stats {
              inningStats {
                home
                away
              }
            }
            competition
          }
        }
      }
      # only homepage
      allTeams: allMarkdownRemark(
        filter: { frontmatter: { mdType: { eq: "team" } } }
      ) {
        edges {
          node {
            frontmatter {
              name
              location {
                fieldName
                town
                abbreviation
              }
              logo {
                childImageSharp {
                  gatsbyImageData(layout: FULL_WIDTH)
                }
              }
            }
          }
        }
      }
      # only homepage
      allEvents: allMarkdownRemark(
        filter: { frontmatter: { mdType: { eq: "event" } } }
        sort: { order: ASC, fields: frontmatter___startDate }
      ) {
        edges {
          node {
            frontmatter {
              mdType
              name
              startDate
              endDate
              highlightColor
            }
          }
        }
      }
      allLeagues: allMarkdownRemark(
        filter: { frontmatter: { mdType: { eq: "league" } } }
      ) {
        edges {
          node {
            frontmatter {
              name
              color
              link
            }
          }
        }
      }
      # not on homepage
      recentNews: allMarkdownRemark(
        filter: {
          frontmatter: {
            templateKey: { eq: "news-post" }
            publish: { eq: true }
          }
        }
        sort: { fields: frontmatter___date, order: DESC }
        limit: 3
      ) {
        nodes {
          id
          fields {
            slug
          }
          frontmatter {
            title
            hero {
              pageImage {
                childImageSharp {
                  gatsbyImageData(aspectRatio: 1, layout: FULL_WIDTH)
                }
              }
            }
            date
          }
          excerpt(pruneLength: 80)
        }
      }

      # rosterHistoryPages: allSitePage(
      #   filter: { path: { regex: "/^/roster/[0-9]{4}$/" } }
      #   sort: { fields: path, order: ASC }
      # ) {
      #   nodes {
      #     path
      #     id
      #   }
      # }

      # scheduleHistoryPages: allSitePage(
      #   filter: { path: { regex: "/^/schedule/[0-9]{4}$/" } }
      #   sort: { fields: path, order: ASC }
      # ) {
      #   nodes {
      #     path
      #     id
      #   }
      # }

      site {
        siteMetadata {
          title
          description
        }
      }
    }
  `);
  addNumberSuffix(data.allGames.nodes);

  return (
    <>
      <GlobalStyles />
      <Seo
        title={seoTitle}
        description={seoDescription}
        titlePrefix={seoTitlePrefix}
        isHomePage={isHomePage}
      />
      <Main>
        <Nav
          isHomePage={isHomePage}
          data={data}
          color={MAIN_COLOR}
          logoSrc={diamondbacksLogo}
          site={SITE}
          homeTeams={HOME_TEAMS}
          getNavData={NAV_DATA}
          socialMediaData={socialMediaData}
        />
        {isHomePage ? (
          <SlideshowHero
            data={data}
            site={SITE}
            color={MAIN_COLOR}
            height={725}
          />
        ) : (
          <WhiteBanner />
        )}
        <Section isHomePage={isHomePage}>
          {!isHomePage && (
            <Breadcrumbs
              seoTitle={seoTitle}
              pageTitle={pageTitle}
              location={location}
              useSeoForTitle={useSeoForTitle}
              site={SITE}
            />
          )}
          <Container isHomePage={isHomePage}>
            <ContentMain noBg={noBg}>{children}</ContentMain>
            <ContentSide>
              <SidePanel
                isHomePage={isHomePage}
                location={location}
                data={data}
                color={MAIN_COLOR}
                logoUrl={BACKUP_LOGO_URL}
                site={SITE}
                timelineAccount={timelineAccount}
                homeTeams={HOME_TEAMS}
              />
            </ContentSide>
          </Container>
        </Section>
        {isHomePage && <Sponsors data={data} />}
        <Footer
          data={data}
          color={MAIN_COLOR}
          getTeamLinks={getTeamLinks}
          facilityLinks={facilityLinks}
          aboutLinks={aboutLinks}
          socialMediaData={socialMediaData}
        />
      </Main>
      <Scroll showBelow={200} />
    </>
  );
};

export default Layout;

const NAV_DATA = (rosterPagePath) => ({
  facilities: {
    links: [
      {
        label: "Glen Hansen Field",
        to: "/facilities/glen-hansen-field",
      },

      {
        label: "Webber Academy",
        to: "/facilities/webber-academy",
      },
    ],
  },
});
